/**
 * Geometry pipeline API client.
 *
 * Provides typed functions for both the VSP adapter (OpenVSP → separate bodies)
 * and import adapter (pre-existing STEP/IGES) workflows, plus shared operations
 * like hinge editing, deflection sweeps, and Flow360 transfer.
 */

import { apiClient } from "./client";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface HingeSpec {
  origin: [number, number, number];
  direction: [number, number, number];
  min_deflection_deg: number;
  max_deflection_deg: number;
}

export interface GeometryBody {
  id: string;
  name: string;
  role: "main" | "control_surface" | "auxiliary";
  step_path: string | null;
  format: string;
  face_count: number;
  is_solid: boolean;
  cs_type?: string;
  hinge?: HingeSpec;
  parent_component?: string;
  bounding_box?: Record<string, number>;
}

export interface Assembly {
  id: string;
  name: string;
  source: "openvsp" | "imported";
  body_count: number;
  control_surface_count: number;
  param_hash: string | null;
  bodies: GeometryBody[];
}

export interface ValidationResult {
  assembly_id: string;
  ready: boolean;
  issues: string[];
}

export interface DeflectionCase {
  case_id: string;
  deflections: Record<string, number>;
  rotation_zones: Record<string, unknown>[];
  config_params: Record<string, number>;
}

export interface DeflectionSweep {
  sweep_id: string;
  case_count: number;
  cases: DeflectionCase[];
}

export interface RotationZone {
  name: string;
  type: string;
  axis: {
    origin: number[];
    direction: number[];
  };
  angle: number;
  units: string;
}

export interface TransferResult {
  folder_id: string;
  assembly_id: string;
  geometries: Record<
    string,
    {
      flow360_geometry_id?: string;
      status: string;
      role: string;
      error?: string;
    }
  >;
}

export interface CaseCreationResult {
  sweep_id: string;
  cases_created: number;
  cases_failed: number;
  cases: Array<{
    flow360_case_id?: string;
    case_name: string;
    deflections: Record<string, number>;
    config_params: Record<string, number>;
    status: string;
    error?: string;
    thread_node_id?: string;
  }>;
}

// ---------------------------------------------------------------------------
// VSP Adapter (Path A)
// ---------------------------------------------------------------------------

export async function processVSP3(params: {
  vsp3_path: string;
  name?: string;
  union_strategy?: string;
  detect_control_surfaces?: boolean;
}): Promise<Assembly> {
  return apiClient<Assembly>("/geometry/vsp/process", {
    method: "POST",
    body: params,
  });
}

// ---------------------------------------------------------------------------
// Import Adapter (Path B)
// ---------------------------------------------------------------------------

export async function importAssembly(params: {
  name: string;
  project_id?: string;
  bodies: Array<{
    name: string;
    role: string;
    cs_type?: string;
    hinge?: HingeSpec;
    parent_component?: string;
  }>;
}): Promise<Assembly> {
  return apiClient<Assembly>("/geometry/import/assembly", {
    method: "POST",
    body: params,
  });
}

// ---------------------------------------------------------------------------
// Assembly Management (shared)
// ---------------------------------------------------------------------------

export async function getAssembly(assemblyId: string): Promise<Assembly> {
  return apiClient<Assembly>(`/geometry/assemblies/${assemblyId}`);
}

export async function validateAssembly(
  assemblyId: string,
): Promise<ValidationResult> {
  return apiClient<ValidationResult>(
    `/geometry/assemblies/${assemblyId}/validate`,
  );
}

export async function updateHinge(
  assemblyId: string,
  surfaceName: string,
  hinge: HingeSpec,
): Promise<{ status: string }> {
  return apiClient(`/geometry/assemblies/${assemblyId}/hinge`, {
    method: "PUT",
    body: { surface_name: surfaceName, hinge },
  });
}

export async function relabelBody(
  assemblyId: string,
  bodyName: string,
  newRole: string,
  csType?: string,
): Promise<{ status: string }> {
  return apiClient(`/geometry/assemblies/${assemblyId}/relabel`, {
    method: "PUT",
    body: { body_name: bodyName, new_role: newRole, cs_type: csType },
  });
}

// ---------------------------------------------------------------------------
// Rotation Zones & Deflection Sweeps
// ---------------------------------------------------------------------------

export async function generateRotationZones(
  assemblyId: string,
  deflections: Record<string, number>,
): Promise<{ zones: RotationZone[]; deflection_count: number }> {
  return apiClient(`/geometry/assemblies/${assemblyId}/rotation-zones`, {
    method: "POST",
    body: deflections,
  });
}

export async function createDeflectionSweep(params: {
  assembly_id: string;
  surfaces: Record<string, number[]>;
  mode?: "full_factorial" | "one_at_a_time" | "paired";
}): Promise<DeflectionSweep> {
  return apiClient<DeflectionSweep>("/geometry/deflection-sweep", {
    method: "POST",
    body: params,
  });
}

export async function generateUDDConfig(
  assemblyId: string,
  surfaceNames?: string[],
): Promise<Record<string, unknown>> {
  return apiClient(`/geometry/assemblies/${assemblyId}/udd-config`, {
    method: "POST",
    body: surfaceNames ?? null,
  });
}

// ---------------------------------------------------------------------------
// Flow360 Transfer
// ---------------------------------------------------------------------------

export async function uploadToFlow360(params: {
  assembly_id: string;
  folder_id?: string;
  project_name?: string;
}): Promise<TransferResult> {
  return apiClient<TransferResult>("/geometry/transfer/upload", {
    method: "POST",
    body: params,
  });
}

export async function createDeflectionCases(params: {
  assembly_id: string;
  surfaces: Record<string, number[]>;
  mode?: string;
  base_case_config: Record<string, unknown>;
  thread_project_id?: string;
  branch?: string;
}): Promise<CaseCreationResult> {
  return apiClient<CaseCreationResult>("/geometry/transfer/cases", {
    method: "POST",
    body: params,
  });
}

// ---------------------------------------------------------------------------
// Control Surface Shape Preview / Gap Check / Export
// ---------------------------------------------------------------------------

export interface CSPreviewParams {
  assembly_id: string;
  surface_name: string;
  eta1: number;
  eta2: number;
  xc1: number;
  xc2: number;
  yh1: number;
  yh2: number;
  gap_mm: number;
  delta_deg: number;
  section_eta: number;
  fillet_mm: number;
}

export interface CSPreviewResult {
  eta: number;
  chord: number;
  hinge_x: number;
  hinge_z: number;
  upper: number[][];
  lower: number[][];
  wing_contour: { x: number[]; z: number[] };
  cs_contour_0: { x: number[]; z: number[] };
  cs_contour_defl: { x: number[]; z: number[] };
  su: { x: number; z: number; radius: number };
  sl: { x: number; z: number; radius: number };
  cove_x: number[];
  cove_z: number[];
  spiral_x: number[];
  spiral_z: number[];
  spiral_defl_x: number[];
  spiral_defl_z: number[];
}

export interface GapCheckParams {
  assembly_id: string;
  surface_name: string;
  eta1: number;
  eta2: number;
  xc1: number;
  xc2: number;
  yh1?: number;
  yh2?: number;
  gap_mm?: number;
  delta_min: number;
  delta_max: number;
  delta_step: number;
  section_eta?: number;
  fillet_mm?: number;
}

export interface GapCheckResultItem {
  delta_deg: number;
  min_gap_mm: number | null;
  location: string;
}

export interface GapCheckResult {
  results: GapCheckResultItem[];
  has_interference: boolean;
}

export interface CSExportStatus {
  job_id: string;
  status: "pending" | "processing" | "complete" | "error";
  step_url: string | null;
  error: string | null;
}

export async function csPreview(
  params: CSPreviewParams,
): Promise<CSPreviewResult> {
  return apiClient<CSPreviewResult>("/geometry/cs-preview", {
    method: "POST",
    body: params,
  });
}

export async function csGapCheck(
  params: GapCheckParams,
): Promise<GapCheckResult> {
  return apiClient<GapCheckResult>("/geometry/cs-gap-check", {
    method: "POST",
    body: params,
  });
}

export async function csExport(params: {
  assembly_id: string;
  surface_name: string;
  eta1: number;
  eta2: number;
  xc_hinge: number;
  mode?: string;
  le_radius_ft?: number;
  gap_ft?: number;
}): Promise<CSExportStatus> {
  return apiClient<CSExportStatus>("/geometry/cs-export", {
    method: "POST",
    body: params,
  });
}

export async function csExportStatus(
  jobId: string,
): Promise<CSExportStatus> {
  return apiClient<CSExportStatus>(`/geometry/cs-export/${jobId}`);
}

// ---------------------------------------------------------------------------
// Deflected Geometry Pipeline (pre-rotated STEP per deflection case)
// ---------------------------------------------------------------------------

export interface JobStatus {
  job_id: string;
  status: "pending" | "processing" | "complete" | "error";
  progress: number;
  message: string;
  result: Record<string, unknown> | null;
  error: string | null;
}

export interface CutDefinition {
  name: string;
  parent_body: string;
  eta1: number;
  eta2: number;
  xc_hinge: number;
  mode: string;
  le_radius: number;
  gap: number;
}

export interface DeflectedSweepResult {
  job_id: string;
  case_count: number;
  status: string;
}

export async function defineCut(
  assemblyId: string,
  cut: CutDefinition,
): Promise<{ status: string; cut_count: number }> {
  return apiClient(`/geometry/assemblies/${assemblyId}/cut-definitions`, {
    method: "POST",
    body: cut,
  });
}

export async function listCuts(
  assemblyId: string,
): Promise<{ cuts: CutDefinition[] }> {
  return apiClient(`/geometry/assemblies/${assemblyId}/cut-definitions`);
}

export async function generateDeflectedGeometry(
  assemblyId: string,
  surfaces: Record<string, number>,
): Promise<{ job_id: string; status: string }> {
  return apiClient(`/geometry/assemblies/${assemblyId}/deflected-geometry`, {
    method: "POST",
    body: { surfaces },
  });
}

export async function getJobStatus(jobId: string): Promise<JobStatus> {
  return apiClient<JobStatus>(`/geometry/jobs/${jobId}`);
}

export async function generateDeflectedSweep(
  assemblyId: string,
  surfaces: Record<string, number[]>,
  mode?: string,
): Promise<DeflectedSweepResult> {
  return apiClient<DeflectedSweepResult>(
    `/geometry/assemblies/${assemblyId}/deflected-sweep`,
    {
      method: "POST",
      body: { surfaces, mode: mode ?? "full_factorial" },
    },
  );
}
